import { useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Pencil,
  Wand2,
  Clapperboard,
  RefreshCw,
  Play,
  ImageIcon,
  Film,
  AlertTriangle,
} from "lucide-react";

type PanelImage = { scene_number?: number; image_url?: string };
type ScenePrompt = { scene_number?: number; prompt?: string; error?: string };
type SceneVideo = { scene_number?: number; videoUrl?: string; status?: string; error?: string };

/**
 * Simplified Video Express Max clip editor. Renders each scene's rendered clip
 * (falling back to the reference panel image while it's still rendering) with a
 * single centered "Edit" button overlaid on the media that opens a full-size
 * modal/dialog. The Edit dialog exposes the three per-clip actions the product
 * calls for:
 *   - Visual edit   → POST scenes/:n/edit { mode: "visual" }  (re-paints the
 *                     reference image, then re-renders the clip)
 *   - Motion edit   → POST scenes/:n/edit { mode: "motion" }  (rewrites the
 *                     video prompt, then re-renders the clip from the same image)
 *   - Re-render product → POST scene-videos/:n/regenerate     (sends the SAME
 *                     panel back to the video model with the SAME prompt)
 * Deliberately omits scene labels, storyboard panels, preview/cut chrome and
 * clip upload — this page is only for fixing individual clips.
 */
export function VideoExpressClipEditor({
  script,
  campaignId,
}: {
  script: any;
  campaignId: string;
}) {
  const { toast } = useToast();
  const scriptsKey = ["/api/video-express-campaigns", campaignId, "scripts"];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: scriptsKey });

  const panelImages: PanelImage[] = ((script.panelImages as PanelImage[]) || [])
    .slice()
    .sort((a, b) => (a.scene_number || 0) - (b.scene_number || 0));
  const scenePrompts: ScenePrompt[] = (script.scenePrompts as ScenePrompt[]) || [];
  const sceneVideos: SceneVideo[] = (script.sceneVideos as SceneVideo[]) || [];

  const promptByScene = new Map<number, ScenePrompt>();
  scenePrompts.forEach((p) => { if (p.scene_number != null) promptByScene.set(p.scene_number, p); });
  const videoByScene = new Map<number, SceneVideo>();
  sceneVideos.forEach((v) => { if (v.scene_number != null) videoByScene.set(v.scene_number, v); });

  // The list of scenes to show: prefer extracted panels (canonical), but fall
  // back to any rendered clips for legacy scripts with no panels.
  const sceneNumbers: number[] = panelImages.length > 0
    ? panelImages.map((p) => p.scene_number || 0)
    : sceneVideos.map((v) => v.scene_number || 0).filter((n, i, a) => a.indexOf(n) === i).sort((a, b) => a - b);

  const [edits, setEdits] = useState<Record<number, string>>({});
  const [motionEdits, setMotionEdits] = useState<Record<number, string>>({});
  const [playing, setPlaying] = useState<Record<string, boolean>>({});
  const [openScene, setOpenScene] = useState<number | null>(null);
  const videoRefs = useRef<Record<number, HTMLVideoElement | null>>({});

  const editMutation = useMutation({
    mutationFn: async ({ n, instruction }: { n: number; instruction: string }) =>
      apiRequest("POST", `/api/video-express-scripts/${script.id}/scenes/${n}/edit`, { instruction, mode: "visual" }),
    onSuccess: (_d, vars) => {
      invalidate();
      setEdits((prev) => { const x = { ...prev }; delete x[vars.n]; return x; });
      toast({ title: `Applying visual edit to clip ${vars.n}…`, description: "Updating the reference image, then re-rendering this clip." });
    },
    onError: (e: any) => toast({ title: "Failed to apply edit", description: e?.message, variant: "destructive" }),
  });

  const motionEditMutation = useMutation({
    mutationFn: async ({ n, instruction }: { n: number; instruction: string }) =>
      apiRequest("POST", `/api/video-express-scripts/${script.id}/scenes/${n}/edit`, { instruction, mode: "motion" }),
    onSuccess: (_d, vars) => {
      invalidate();
      setMotionEdits((prev) => { const x = { ...prev }; delete x[vars.n]; return x; });
      toast({ title: `Updating motion for clip ${vars.n}…`, description: "Rewriting the video prompt, then re-rendering this clip." });
    },
    onError: (e: any) => toast({ title: "Failed to apply motion edit", description: e?.message, variant: "destructive" }),
  });

  const reRenderMutation = useMutation({
    mutationFn: async (n: number) => apiRequest("POST", `/api/video-express-scripts/${script.id}/scene-videos/${n}/regenerate`),
    onSuccess: (_d, n) => { invalidate(); toast({ title: `Re-rendering clip ${n}…`, description: "Sending the same panel back to the video model with the same prompt." }); },
    onError: (e: any) => toast({ title: "Failed to re-render clip", description: e?.message, variant: "destructive" }),
  });

  if (sceneNumbers.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border/40 bg-muted/[0.03] py-10 flex flex-col items-center text-center gap-2" data-testid={`clip-editor-empty-${script.id}`}>
        <Loader2 className="h-5 w-5 text-muted-foreground/50 animate-spin" />
        <p className="text-xs text-muted-foreground">Clips appear here as soon as they're rendered.</p>
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
      data-testid={`clip-editor-${script.id}`}
    >
      {sceneNumbers.map((n) => {
        const panel = panelImages.find((p) => (p.scene_number || 0) === n);
        const prompt = promptByScene.get(n);
        const video = videoByScene.get(n);
        const instruction = edits[n] || "";
        const motionInstruction = motionEdits[n] || "";
        const editingThis = editMutation.isPending && editMutation.variables?.n === n;
        const motionEditingThis = motionEditMutation.isPending && motionEditMutation.variables?.n === n;
        const reRenderThis = reRenderMutation.isPending && reRenderMutation.variables === n;
        const clipPending = !!video && !video.videoUrl && !video.error && video.status !== "paused";
        const busy = editingThis || motionEditingThis || reRenderThis || clipPending;
        const hasClip = !!video?.videoUrl;
        const rendering = (reRenderThis || clipPending) && !editingThis && !motionEditingThis;
        const playKey = video?.videoUrl || "";

        return (
          <div
            key={n}
            className="group relative aspect-[9/16] overflow-hidden rounded-xl border border-card-border bg-gradient-to-b from-muted/40 to-muted/20 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg"
            data-testid={`clip-${script.id}-${n}`}
          >
            {/* Media: the rendered clip if present, otherwise the reference image. */}
            {hasClip ? (
              <>
                <video
                  key={playKey}
                  ref={(el) => { videoRefs.current[n] = el; }}
                  src={video!.videoUrl}
                  poster={panel?.image_url || undefined}
                  controls={!!playing[playKey]}
                  preload="none"
                  playsInline
                  onPlay={() => setPlaying((p) => ({ ...p, [playKey]: true }))}
                  onPause={() => setPlaying((p) => ({ ...p, [playKey]: false }))}
                  onEnded={() => setPlaying((p) => ({ ...p, [playKey]: false }))}
                  onAbort={() => setPlaying((p) => ({ ...p, [playKey]: false }))}
                  onEmptied={() => setPlaying((p) => ({ ...p, [playKey]: false }))}
                  className="absolute inset-0 h-full w-full object-cover"
                  data-testid={`video-clip-${n}`}
                />
                {!playing[playKey] && (
                  <button
                    type="button"
                    onClick={() => { const v = videoRefs.current[n]; if (v) v.play().catch(() => {}); }}
                    className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-t from-black/40 via-transparent to-black/10"
                    aria-label={`Play clip ${n}`}
                    data-testid={`button-play-clip-${n}`}
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white shadow-lg ring-1 ring-white/40 backdrop-blur-md transition-transform duration-200 group-hover:scale-110">
                      <Play className="h-5 w-5 translate-x-[1px] fill-white" />
                    </span>
                  </button>
                )}
              </>
            ) : panel?.image_url ? (
              <img
                src={panel.image_url}
                alt={`Clip ${n} reference`}
                className="absolute inset-0 h-full w-full object-cover"
                data-testid={`img-clip-${n}`}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <ImageIcon className="h-5 w-5 text-muted-foreground/40" />
              </div>
            )}

            {/* Render / busy overlay */}
            {rendering ? (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-background/75 text-[10px] font-medium text-foreground backdrop-blur-sm">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                Rendering…
              </div>
            ) : (editingThis || motionEditingThis) ? (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-2 bg-background/75 text-[10px] font-medium text-foreground backdrop-blur-sm">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                {editingThis ? "Applying edit…" : "Updating motion…"}
              </div>
            ) : null}

            {/* Failed clip state: clear message + one-click re-run (the existing
                per-scene regenerate). While the retry is pending, `rendering`
                takes over and shows the normal spinner overlay instead. */}
            {video?.error && !rendering && !editingThis && !motionEditingThis && (
              <div
                className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-2 bg-background/85 px-3 text-center backdrop-blur-sm"
                title={`Generation failed: ${video.error}`}
                data-testid={`text-clip-error-${n}`}
              >
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <p className="text-[11px] font-medium text-foreground">Generation failed</p>
                <Button
                  size="sm"
                  className="h-8 rounded-full px-4 text-xs font-medium shadow-lg"
                  disabled={busy}
                  onClick={() => reRenderMutation.mutate(n)}
                  data-testid={`button-rerun-clip-${n}`}
                >
                  {reRenderThis ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1.5 h-3.5 w-3.5" />}
                  Re-run
                </Button>
                <button
                  type="button"
                  className="text-[10px] text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  onClick={() => setOpenScene(n)}
                  data-testid={`button-edit-failed-clip-${n}`}
                >
                  Edit instead
                </button>
              </div>
            )}

            {/* No-clip hint */}
            {!hasClip && !rendering && !video?.error && !editingThis && !motionEditingThis && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center gap-1 bg-gradient-to-t from-black/60 to-transparent px-2 pb-1.5 pt-5 text-[9px] font-medium text-white/90">
                <Film className="h-3 w-3 shrink-0" />
                <span className="truncate">No clip yet</span>
              </div>
            )}

            {/* Centered Edit affordance — hover-revealed so it doesn't block viewing.
                Pointer events stay off until hover so the underlying play button
                (and video controls while playing) remain clickable. Suppressed
                while the failed-state overlay is up so its Re-run button stays
                clickable (that overlay has its own "Edit instead" link). */}
            <div
              className={`absolute inset-0 z-40 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-200 pointer-events-none ${playing[playKey] || (video?.error && !rendering) ? "" : "group-hover:opacity-100 group-hover:pointer-events-auto"}`}
            >
              <Button
                size="sm"
                variant="secondary"
                className="h-9 rounded-full px-4 text-xs font-medium shadow-lg"
                onClick={() => setOpenScene(n)}
                data-testid={`button-edit-clip-${n}`}
              >
                {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Pencil className="mr-1.5 h-3.5 w-3.5" />}
                Edit
              </Button>
            </div>
          </div>
        );
      })}

      {/* Full-size edit modal for the selected panel */}
      <Dialog open={openScene !== null} onOpenChange={(o) => { if (!o) setOpenScene(null); }}>
        {openScene !== null && (() => {
          const n = openScene;
          const panel = panelImages.find((p) => (p.scene_number || 0) === n);
          const prompt = promptByScene.get(n);
          const video = videoByScene.get(n);
          const instruction = edits[n] || "";
          const motionInstruction = motionEdits[n] || "";
          const editingThis = editMutation.isPending && editMutation.variables?.n === n;
          const motionEditingThis = motionEditMutation.isPending && motionEditMutation.variables?.n === n;
          const reRenderThis = reRenderMutation.isPending && reRenderMutation.variables === n;
          const clipPending = !!video && !video.videoUrl && !video.error && video.status !== "paused";
          const busy = editingThis || motionEditingThis || reRenderThis || clipPending;
          const hasClip = !!video?.videoUrl;
          const rendering = (reRenderThis || clipPending) && !editingThis && !motionEditingThis;

          return (
            <DialogContent
              className="max-w-3xl gap-0 p-0 overflow-hidden"
              data-testid={`dialog-edit-clip-${n}`}
            >
              <DialogHeader className="px-5 pt-5 pb-3">
                <DialogTitle className="text-sm">Edit clip {n}</DialogTitle>
              </DialogHeader>

              <div className="grid gap-5 px-5 pb-5 md:grid-cols-[minmax(0,1fr)_320px]">
                {/* Large media */}
                <div className="relative mx-auto aspect-[9/16] w-full max-w-[280px] overflow-hidden rounded-xl border border-card-border bg-muted/20 md:max-w-none">
                  {hasClip ? (
                    <video
                      key={video!.videoUrl}
                      src={video!.videoUrl}
                      poster={panel?.image_url || undefined}
                      controls
                      preload="metadata"
                      playsInline
                      className="absolute inset-0 h-full w-full object-contain bg-black"
                      data-testid={`dialog-video-clip-${n}`}
                    />
                  ) : panel?.image_url ? (
                    <img
                      src={panel.image_url}
                      alt={`Clip ${n} reference`}
                      className="absolute inset-0 h-full w-full object-cover"
                      data-testid={`dialog-img-clip-${n}`}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <ImageIcon className="h-6 w-6 text-muted-foreground/40" />
                    </div>
                  )}

                  {/* Render / edit busy overlay */}
                  {rendering ? (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-background/75 text-xs font-medium text-foreground backdrop-blur-sm">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      Rendering…
                    </div>
                  ) : (editingThis || motionEditingThis) ? (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-background/75 text-xs font-medium text-foreground backdrop-blur-sm">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      {editingThis ? "Applying edit…" : "Updating motion…"}
                    </div>
                  ) : null}

                  {/* Failed clip ribbon + one-click re-run */}
                  {video?.error && !rendering && (
                    <div
                      className="absolute inset-x-0 bottom-0 z-10 flex items-center gap-1.5 bg-gradient-to-t from-red-600/95 to-red-500/85 px-3 py-1.5 text-[11px] font-medium text-white"
                      title={`Generation failed: ${video.error}`}
                      data-testid={`dialog-text-clip-error-${n}`}
                    >
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">Generation failed: {video.error}</span>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="ml-auto h-6 shrink-0 rounded-full px-2.5 text-[10px] font-medium"
                        disabled={busy}
                        onClick={() => reRenderMutation.mutate(n)}
                        data-testid={`dialog-button-rerun-clip-${n}`}
                      >
                        {reRenderThis ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-1 h-3 w-3" />}
                        Re-run
                      </Button>
                    </div>
                  )}

                  {/* No-clip hint */}
                  {!hasClip && !rendering && !video?.error && !editingThis && !motionEditingThis && (
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-center gap-1.5 bg-gradient-to-t from-black/60 to-transparent px-3 pb-2 pt-6 text-[11px] font-medium text-white/90">
                      <Film className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">No clip yet — showing reference image</span>
                    </div>
                  )}
                </div>

                {/* Controls */}
                <div className="space-y-4">
                  {/* Visual edit */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Visual edit</p>
                    <Textarea
                      value={instruction}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [n]: e.target.value }))}
                      placeholder="Change how this clip looks (e.g. make the mug blue, reveal the product more)…"
                      className="min-h-[72px] resize-y text-xs leading-relaxed"
                      disabled={busy}
                      data-testid={`textarea-visual-edit-${n}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-full rounded-full text-xs"
                      disabled={!instruction.trim() || busy}
                      onClick={() => editMutation.mutate({ n, instruction: instruction.trim() })}
                      data-testid={`button-apply-visual-edit-${n}`}
                    >
                      {editingThis ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Applying…</> : <><Wand2 className="mr-1.5 h-3.5 w-3.5" /> Apply Visual Edit</>}
                    </Button>
                  </div>

                  {/* Motion edit */}
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Motion edit</p>
                    <Textarea
                      value={motionInstruction}
                      onChange={(e) => setMotionEdits((prev) => ({ ...prev, [n]: e.target.value }))}
                      placeholder="Change the action or motion (e.g. slow zoom in, she turns to the camera, faster pace)…"
                      className="min-h-[72px] resize-y text-xs leading-relaxed"
                      disabled={busy}
                      data-testid={`textarea-motion-edit-${n}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-full rounded-full text-xs"
                      disabled={!motionInstruction.trim() || busy}
                      onClick={() => motionEditMutation.mutate({ n, instruction: motionInstruction.trim() })}
                      data-testid={`button-apply-motion-edit-${n}`}
                    >
                      {motionEditingThis ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Rewriting…</> : <><Clapperboard className="mr-1.5 h-3.5 w-3.5" /> Apply Motion Edit</>}
                    </Button>
                  </div>

                  {prompt?.error && (
                    <p className="text-[11px] text-red-500" data-testid={`text-prompt-error-${n}`}>
                      Prompt error: {prompt.error}
                    </p>
                  )}

                  {/* Re-render product: same panel + same prompt → video model */}
                  <div className="border-t border-border/40 pt-3">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-full rounded-full text-xs"
                      disabled={busy}
                      onClick={() => reRenderMutation.mutate(n)}
                      data-testid={`button-rerender-product-${n}`}
                    >
                      {reRenderThis ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Re-rendering…</> : <><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Re-render Product</>}
                    </Button>
                    <p className="mt-1.5 text-[10px] text-muted-foreground/50">Re-renders this clip from the same panel and prompt — no changes.</p>
                  </div>
                </div>
              </div>
            </DialogContent>
          );
        })()}
      </Dialog>
    </div>
  );
}
